import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.set.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { isValidPostMessage, createPostMessage } from '../utils/protocol';
import { isWindowAvailable } from '../utils/window';
import { OriginConstant } from '../constants';
import { requestIframeLog } from '../utils/logger';
import { SyncHook } from '../utils/hooks';

/**
 * Message context (extracted from MessageEvent, transport-agnostic)
 */
export var MessageContextStage = {
  /** Message is not handled by any instance yet */
  PENDING: 'pending',
  /** Message is claimed/being handled (handledBy is set, but not accepted) */
  HANDLING: 'handling',
  /** Message is accepted for processing (acceptedBy is set) */
  ACCEPTED: 'accepted',
  /** Message handling finished (synchronous dispatch finished) */
  DONE: 'done'
};

/**
 * Message receiver callback
 */

/**
 * Channel type constants
 */
export var ChannelType = {
  /** postMessage channel type */
  POST_MESSAGE: 'postMessage'
};

/**
 * Channel type
 */

/**
 * MessageChannel - Low-level communication channel for postMessage
 * 
 * Responsibilities:
 * - Listening to window.postMessage events
 * - Filtering messages by secretKey
 * - Extracting transport-specific information (MessageEvent) into generic MessageContext
 * - Forwarding received messages to registered receivers
 * - Sending messages to target windows
 * 
 * This is the low-level communication layer that handles postMessage directly.
 * All transport-specific details (like MessageEvent) are encapsulated here.
 */
export class MessageChannel {
  constructor(secretKey, type = ChannelType.POST_MESSAGE) {
    /** Channel type (used for cache isolation) */
    /** Secret key for message isolation */
    /** Message receiver callbacks (support multiple receivers) */
    _defineProperty(this, "receivers", new Set());
    /** Reference count (for cache management) */
    _defineProperty(this, "refCount", 0);
    this.type = type;
    this.secretKey = secretKey;
    this.boundOnMessage = this.onMessage.bind(this);
    window.addEventListener('message', this.boundOnMessage);
  }

  /**
   * Add message receiver callback
   * When a message is received, it will be forwarded to all registered receivers
   */
  addReceiver(receiver) {
    this.receivers.add(receiver);
  }

  /**
   * Remove message receiver callback
   */
  removeReceiver(receiver) {
    this.receivers.delete(receiver);
  }

  /**
   * Increment reference count (for cache management)
   */
  addRef() {
    this.refCount++;
  }

  /**
   * Decrement reference count (for cache management)
   * @returns current reference count
   */
  release() {
    return --this.refCount;
  }

  /**
   * Get reference count (for cache management)
   */
  getRefCount() {
    return this.refCount;
  }

  /**
   * Extract MessageContext from MessageEvent
   * This encapsulates transport-specific details
   */
  extractContext(event) {
    var _ref;
    var context = {
      source: (_ref = event.source) !== null && _ref !== void 0 ? _ref : undefined,
      origin: event.origin
    };
    var stateChangeHook;
    var ensureStateChangeHook = () => {
      if (!stateChangeHook) {
        stateChangeHook = new SyncHook();
      }
      return stateChangeHook;
    };
    var emitStateChange = (prev, next) => {
      if (!stateChangeHook) return;
      stateChangeHook.call(prev, next, context);
    };

    /**
     * Attach stable internal helper methods.
     *
     * We intentionally make them non-enumerable so typical object inspection won't be polluted.
     */
    var markHandledBy = handledBy => {
      var prev = getStage();
      if (!context.handledBy) {
        context.handledBy = handledBy;
      }
      var next = getStage();
      if (prev !== next) emitStateChange(prev, next);
    };
    var markAcceptedBy = handledBy => {
      var prev = getStage();
      if (!context.acceptedBy) {
        context.acceptedBy = handledBy;
      }
      markHandledBy(handledBy);
      var next = getStage();
      if (prev !== next) emitStateChange(prev, next);
    };
    var markDoneBy = doneBy => {
      var prev = getStage();
      if (!context.doneBy) {
        context.doneBy = doneBy;
      }
      var next = getStage();
      if (prev !== next) emitStateChange(prev, next);
    };
    var getStage = () => {
      if (context.doneBy) return MessageContextStage.DONE;
      if (context.acceptedBy) return MessageContextStage.ACCEPTED;
      if (context.handledBy) return MessageContextStage.HANDLING;
      return MessageContextStage.PENDING;
    };
    var onStateChange = (name, fn) => {
      return ensureStateChangeHook().tap(name, fn);
    };
    Object.defineProperty(context, 'markHandledBy', {
      value: markHandledBy,
      enumerable: false
    });
    Object.defineProperty(context, 'markAcceptedBy', {
      value: markAcceptedBy,
      enumerable: false
    });
    Object.defineProperty(context, 'markDoneBy', {
      value: markDoneBy,
      enumerable: false
    });
    Object.defineProperty(context, 'getStage', {
      value: getStage,
      enumerable: false
    });
    Object.defineProperty(context, 'onStateChange', {
      value: onStateChange,
      enumerable: false
    });
    return context;
  }

  /**
   * Message handling entry point
   */
  onMessage(event) {
    var data = event.data;

    // Check if this is a request-iframe framework message (basic format validation)
    if (!isValidPostMessage(data)) {
      return;
    }

    // secretKey isolation
    if (this.secretKey) {
      if (data.secretKey !== this.secretKey) return;
    } else {
      if (data.secretKey) return;
    }

    // Extract context from transport-specific event
    var context = this.extractContext(event);

    // Forward to all registered receivers
    this.receivers.forEach(receiver => {
      try {
        receiver(data, context);
      } catch (e) {
        requestIframeLog('error', 'Receiver error', e);
      }
    });
  }

  /**
   * Send raw message to target window
   * @param target target window
   * @param message message data (already formatted as PostMessageData)
   * @param targetOrigin target origin (defaults to '*')
   */
  send(target, message, targetOrigin = OriginConstant.ANY, transfer) {
    if (!isWindowAvailable(target)) {
      return false;
    }
    /**
     * Prefer transferable objects when provided.
     * Use a try/catch fallback for environments that don't support the 3rd arg signature.
     */
    if (transfer && transfer.length) {
      try {
        target.postMessage(message, targetOrigin, transfer);
        return true;
      } catch (_unused) {
        /** fall through to 2-arg postMessage */
      }
    }
    target.postMessage(message, targetOrigin);
    return true;
  }

  /**
   * Send typed message to target window (creates PostMessageData automatically)
   * @param target target window
   * @param targetOrigin target origin
   * @param type message type
   * @param requestId request ID
   * @param data additional data
   */
  sendMessage(target, targetOrigin, type, requestId, data, transfer) {
    var message = createPostMessage(type, requestId, _objectSpread(_objectSpread({}, data), {}, {
      secretKey: this.secretKey
    }));
    return this.send(target, message, targetOrigin, transfer);
  }

  /**
   * Add path prefix (for secretKey isolation)
   */
  prefixPath(path) {
    return this.secretKey ? `${this.secretKey}:${path}` : path;
  }

  /**
   * Destroy channel (remove event listener)
   */
  destroy() {
    window.removeEventListener('message', this.boundOnMessage);
    this.receivers.clear();
  }
}